import React, { useEffect, useMemo, useState } from 'react';
import Modal from './Modal';

const API_BASE = process.env.REACT_APP_API_URL || '';

function buildUrl(path) {
  return `${API_BASE.replace(/\/$/, '')}${path}`;
}

function imageSrc(path) {
  if (!path) return '';
  const p = String(path).trim();
  if (typeof window !== 'undefined' && p.startsWith('/uploads/')) {
    return window.location.origin + (p.startsWith('/') ? p : '/' + p);
  }
  if (p.startsWith('http://') || p.startsWith('https://')) return p;
  if (typeof window !== 'undefined') return window.location.origin + (p.startsWith('/') ? p : '/' + p);
  return p;
}

function GalleryOne() {
    const TILE_COUNT = 12;

    // Different tile sizes to create a creative collage layout.
    // Desktop uses row/col spans; tablet and mobile override them for responsiveness.
    const VARIANTS = useMemo(() => ([
        'variant-a', // big
        'variant-d', // small-tall
        'variant-b', // medium
        'variant-c', // tall
        'variant-e', // tiny
        'variant-f', // wide
        'variant-d',
        'variant-b',
        'variant-c',
        'variant-e',
        'variant-a',
        'variant-f',
    ]), []);

    const [tiles, setTiles] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalImage, setModalImage] = useState('');

    useEffect(() => {
        let cancelled = false;

        async function load() {
            try {
                const res = await fetch(buildUrl('/api/gallery'));
                const data = await res.json().catch(() => ({}));
                const apiItems = data && data.success && Array.isArray(data.items) ? data.items : [];
                const apiSources = apiItems
                    .map((it) => imageSrc(it.image))
                    .filter((s) => Boolean(s));

                if (!cancelled) setTiles(apiSources.slice(0, TILE_COUNT));
            } catch (_e) {
                if (!cancelled) setTiles([]);
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, []);

    const openModal = (src) => {
        setModalImage(src);
        setIsModalOpen(true);
    };

    const closeModal = () => setIsModalOpen(false);

    return (
        <div className="gallery-area">
            <div className="container th-container shape-mockup-wrap">
                <div className="title-area text-center">
                    <span className="sub-title">Highlights From Our Programs</span>
                    <h2 className="sec-title">Recent Gallery</h2>
                </div>
                <style>{`
                    /* Creative CSS-grid collage (self-contained; no external CSS changes) */
                    .gallery-one-collage-grid {
                        display: grid;
                        grid-auto-flow: dense;
                        gap: 12px;
                        grid-template-columns: repeat(6, 1fr);
                        grid-auto-rows: 16px; /* controls masonry-ish vertical rhythm */
                    }

                    .gallery-one-collage-item {
                        border: none;
                        padding: 0;
                        margin: 0;
                        background: transparent;
                        border-radius: 18px;
                        overflow: hidden;
                        cursor: pointer;
                        display: block;
                        width: 100%;
                        height: 100%;
                    }

                    .gallery-one-collage-item img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                        display: block;
                        transform: scale(1);
                        transition: transform 320ms ease;
                    }

                    .gallery-one-collage-item:hover img {
                        transform: scale(1.07);
                    }

                    /* Desktop (6 columns) spans */
                    .gallery-one-collage-item.variant-a { grid-column: span 3; grid-row: span 16; }
                    .gallery-one-collage-item.variant-b { grid-column: span 2; grid-row: span 12; }
                    .gallery-one-collage-item.variant-c { grid-column: span 2; grid-row: span 18; }
                    .gallery-one-collage-item.variant-d { grid-column: span 1; grid-row: span 12; }
                    .gallery-one-collage-item.variant-e { grid-column: span 1; grid-row: span 8; }
                    .gallery-one-collage-item.variant-f { grid-column: span 3; grid-row: span 12; }

                    /* Tablet: 2–3 columns */
                    @media (max-width: 991px) {
                        .gallery-one-collage-grid { grid-template-columns: repeat(3, 1fr); }
                        .gallery-one-collage-item.variant-a { grid-column: span 2; grid-row: span 14; }
                        .gallery-one-collage-item.variant-b { grid-column: span 1; grid-row: span 12; }
                        .gallery-one-collage-item.variant-c { grid-column: span 1; grid-row: span 14; }
                        .gallery-one-collage-item.variant-d { grid-column: span 1; grid-row: span 12; }
                        .gallery-one-collage-item.variant-e { grid-column: span 1; grid-row: span 10; }
                        .gallery-one-collage-item.variant-f { grid-column: span 2; grid-row: span 12; }
                    }

                    @media (max-width: 768px) {
                        .gallery-one-collage-grid { grid-template-columns: repeat(2, 1fr); }
                    }

                    /* Mobile: 1 column */
                    @media (max-width: 576px) {
                        .gallery-one-collage-grid { grid-template-columns: repeat(1, 1fr); }
                        .gallery-one-collage-item.variant-a { grid-column: span 1; grid-row: span 16; }
                        .gallery-one-collage-item.variant-b { grid-column: span 1; grid-row: span 12; }
                        .gallery-one-collage-item.variant-c { grid-column: span 1; grid-row: span 16; }
                        .gallery-one-collage-item.variant-d { grid-column: span 1; grid-row: span 14; }
                        .gallery-one-collage-item.variant-e { grid-column: span 1; grid-row: span 10; }
                        .gallery-one-collage-item.variant-f { grid-column: span 1; grid-row: span 12; }
                    }
                `}</style>

                <div className="gallery-one-collage-grid">
                    {tiles.map((src, idx) => (
                        <button
                            key={`${idx}-${src}`}
                            type="button"
                            className={`gallery-one-collage-item ${VARIANTS[idx % VARIANTS.length]}`}
                            aria-label={`Open gallery image ${idx + 1}`}
                            onClick={() => openModal(src)}
                        >
                            <img src={src} alt={`Gallery item ${idx + 1}`} />
                        </button>
                    ))}
                </div>
            </div>
            <Modal isOpen={isModalOpen} closeModal={closeModal} imageSrc={modalImage} />
        </div>
    );
}

export default GalleryOne;
